import { useMutation, useQuery } from 'react-query'
import { useSupabaseClient } from '@dolfin/hooks/supabase'
import { Database } from '@dolfin/business/db_types'
import { useRouter as useNavigatorRouter } from 'next/navigation'
import type { Factor } from '@supabase/auth-js'
import { Capacitor } from '@capacitor/core'
import { Browser } from '@capacitor/browser'
import posthog from 'posthog-js'
import { getBaseUrl } from '@dolfin/utils/environment'

interface MFAVerifyData {
  factorId: string
  challengeId: string
  code: string
}

function sanitizeRedirectPath(value: string | undefined, baseUrl: string) {
  if (!value) {
    return null
  }

  if (value.startsWith('/') && !value.startsWith('//')) {
    return value
  }

  try {
    const redirectUrl = new URL(value)
    const appBaseUrl = new URL(baseUrl)

    if (redirectUrl.origin === appBaseUrl.origin) {
      return `${redirectUrl.pathname}${redirectUrl.search}${redirectUrl.hash}`
    }
  } catch {
    return null
  }

  return null
}

function firstStringValue(value?: string | string[]) {
  if (Array.isArray(value)) {
    return value[0]
  }

  return value
}

export function resolvePostLoginDestination(args: {
  next?: string | string[]
  redirectedFrom?: string | string[]
  page?: string | string[]
  baseUrl: string
}) {
  const page = firstStringValue(args.page)

  if (page) {
    return `/dashboard?page=${encodeURIComponent(page)}`
  }

  return (
    sanitizeRedirectPath(firstStringValue(args.next), args.baseUrl) ??
    sanitizeRedirectPath(firstStringValue(args.redirectedFrom), args.baseUrl) ??
    '/dashboard'
  )
}

export function buildAuthCallbackRedirectTo(args: {
  baseUrl: string
  currentSearch?: string
  isNative: boolean
}) {
  const params = new URLSearchParams(args.currentSearch ?? '')
  const next = params.get('next') ?? params.get('redirectedFrom')
  const page = params.get('page')

  if (args.isNative) {
    const nativeRedirectUrl = new URL('dolfin://auth/callback')

    if (next) {
      nativeRedirectUrl.searchParams.set('next', next)
    }

    if (page) {
      nativeRedirectUrl.searchParams.set('page', page)
    }

    return nativeRedirectUrl.toString()
  }

  const redirectUrl = new URL('/auth/callback', args.baseUrl)

  if (next) {
    redirectUrl.searchParams.set('next', next)
  }

  if (page) {
    redirectUrl.searchParams.set('page', page)
  }

  return redirectUrl.toString()
}

export const useSignup = (onSuccess: () => void, onError: (error: Error) => void) => {
  const supabase = useSupabaseClient<Database>()

  return useMutation(
    'signup',
    async (credentials: { email: string; name: string; password: string }) => {
      const { error } = await supabase.auth.signUp({
        email: credentials.email,
        password: credentials.password,
        options: {
          emailRedirectTo: `${getBaseUrl()}/signup/email-confirmed`,
          data: {
            name: credentials.name
          }
        }
      })

      if (error) {
        throw error
      }
    },
    { onError, onSuccess }
  )
}

export const useAcceptInvite = (onSuccess: () => void, onError: (error: Error) => void) => {
  const supabase = useSupabaseClient<Database>()

  return useMutation(
    'accept_invite',
    async (credentials: { accessToken?: string; refreshToken?: string; password: string }) => {
      if (credentials.accessToken && credentials.refreshToken) {
        await supabase.auth.setSession({
          access_token: credentials.accessToken,
          refresh_token: credentials.refreshToken
        })
      }

      const { error } = await supabase.auth.updateUser({
        password: credentials.password
      })

      if (error) {
        throw error
      }
    },
    {
      onError,
      onSuccess
    }
  )
}

export const useSignOut = () => {
  const supabase = useSupabaseClient<Database>()
  const location = useNavigatorRouter()

  return async () => {
    posthog.capture('user_signed_out')
    posthog.reset()

    const { error } = await supabase.auth.signOut()
    if (error) {
      throw error
    }

    location.push('/login')
  }
}

export const useSignInWithPassword = (onSuccess: () => void, onError: (error: Error) => void) => {
  const supabase = useSupabaseClient<Database>()

  return useMutation(
    'signin',
    async (credentials: { email: string; password: string }) => {
      const { data, error } = await supabase.auth.signInWithPassword(credentials)

      if (error) {
        throw error
      }

      return data
    },
    { onError, onSuccess }
  )
}

export const useSignInWithOAuth = (onSuccess: () => void, onError: (error: Error) => void) => {
  const supabase = useSupabaseClient<Database>()

  return useMutation(
    async (provider: 'google' | 'apple' | 'azure') => {
      const isNative = Capacitor.isNativePlatform()
      const redirectTo = buildAuthCallbackRedirectTo({
        baseUrl: getBaseUrl(),
        currentSearch: typeof window !== 'undefined' ? window.location.search : undefined,
        isNative
      })

      const { data, error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo,
          scopes: provider === 'azure' ? 'email' : undefined,
          skipBrowserRedirect: isNative ? true : undefined
        }
      })

      if (error) {
        throw error
      }
      if (data?.url) {
        if (isNative) {
          await Browser.open({
            url: data.url,
            presentationStyle: 'popover'
          })
        } else {
          window.location.href = data.url
        }
      }

      return data
    },
    { onError, onSuccess }
  )
}

export const useSignInWithSSO = (
  email: string,
  onSuccess: () => void,
  onError: (error: Error) => void
) => {
  const supabase = useSupabaseClient<Database>()

  return useMutation(
    async () => {
      const isNative = Capacitor.isNativePlatform()
      const redirectTo = buildAuthCallbackRedirectTo({
        baseUrl: getBaseUrl(),
        currentSearch: typeof window !== 'undefined' ? window.location.search : undefined,
        isNative
      })

      const { data, error } = await supabase.auth.signInWithSSO({
        domain: email.split('@')[1],
        options: {
          redirectTo
        }
      })

      if (error) {
        throw error
      }

      if (data?.url) {
        if (isNative) {
          await Browser.open({ url: data.url })
        } else {
          window.location.href = data.url
        }
      }
    },
    { onError, onSuccess }
  )
}

export const useEnrollMfa = (
  onSuccess: (data: {
    id: string
    type: 'totp'
    totp: { qr_code: string; secret: string; uri: string }
  }) => void,
  onError: (error: Error) => void
) => {
  const supabase = useSupabaseClient<Database>()

  return useMutation(
    'mfaEnroll',
    async () => {
      const { error, data } = await supabase.auth.mfa.enroll({
        factorType: 'totp'
      })

      if (error) {
        throw error
      }

      return data
    },
    { onError, onSuccess }
  )
}

export const useUnenrrollMfa = (onSuccess: () => void, onError: (error: Error) => void) => {
  const supabase = useSupabaseClient<Database>()

  return useMutation(
    'mfaUnEnroll',
    async (factorId: string) => {
      const { error, data } = await supabase.auth.mfa.unenroll({
        factorId
      })

      if (error) {
        throw error
      }

      return data
    },
    { onError, onSuccess }
  )
}

export const useMfaChallenge = (
  onSuccess: (data: { id: string; expires_at: number } | null) => void,
  onError: (error: Error) => void
) => {
  const supabase = useSupabaseClient<Database>()

  return useMutation(
    'mfaChallenge',
    async (factorId: string) => {
      const { error, data } = await supabase.auth.mfa.challenge({
        factorId
      })

      if (error) {
        throw error
      }

      return data
    },
    { onError, onSuccess }
  )
}

export const useMfaVerify = (onSuccess: () => void, onError: (error: Error) => void) => {
  const supabase = useSupabaseClient<Database>()

  return useMutation(
    'mfaVerify',
    async (data: MFAVerifyData) => {
      const { error } = await supabase.auth.mfa.verify(data)

      if (error) {
        throw error
      }
    },
    { onError, onSuccess }
  )
}

export const useMfaListFactors = (
  onSuccess: (factors: Factor[]) => void,
  onError: (error: Error) => void
) => {
  const supabase = useSupabaseClient<Database>()

  return useQuery(
    'mfaListFactors',
    async () => {
      const { data, error } = await supabase.auth.mfa.listFactors()

      if (error) {
        throw error
      }

      return data.totp
    },
    { onError, onSuccess }
  )
}
